// src/core/script-blocker.ts

import type { ConsentState } from '../types'
import { debugLog } from '../utils/helpers'

const BLOCKED_SCRIPTS = new Set<HTMLScriptElement>()
const BLOCKED_IFRAMES = new Set<HTMLIFrameElement>()
const UNBLOCKED_SCRIPTS: { src: string; category: string; timestamp: number }[] = []

/**
 * Block all scripts with data-cookie-category attribute
 */
export function blockScripts(): void {
  if (typeof document === 'undefined') return

  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[data-cookie-category][type="text/plain"]'
  )

  scripts.forEach(script => {
    BLOCKED_SCRIPTS.add(script)
    debugLog('Script blocked', script.src || script.textContent?.substring(0, 50))
  })

  debugLog(`Blocked ${BLOCKED_SCRIPTS.size} scripts`)
}

/**
 * Block all iframes with data-cookie-category attribute
 */
export function blockIframes(): void {
  if (typeof document === 'undefined') return

  const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[data-cookie-category]')

  iframes.forEach(iframe => {
    const originalSrc = iframe.src
    if (originalSrc) {
      iframe.dataset.originalSrc = originalSrc
      iframe.src = ''
      BLOCKED_IFRAMES.add(iframe)
      debugLog('Iframe blocked', originalSrc)
    }
  })

  debugLog(`Blocked ${BLOCKED_IFRAMES.size} iframes`)
}

/**
 * Unblock scripts based on consent
 */
export function unblockScripts(consent: ConsentState, onUnblock?: (src: string) => void): void {
  if (typeof document === 'undefined') return

  const scriptsToUnblock: HTMLScriptElement[] = []

  BLOCKED_SCRIPTS.forEach(script => {
    const category = script.dataset.cookieCategory
    if (!category) return

    // Check if user consented to this category
    if (consent[category]) {
      scriptsToUnblock.push(script)
    }
  })

  // Unblock scripts by changing type and re-executing
  scriptsToUnblock.forEach(oldScript => {
    const newScript = document.createElement('script')
    const category = oldScript.dataset.cookieCategory || 'unknown'

    // Copy all attributes
    Array.from(oldScript.attributes).forEach(attr => {
      if (attr.name !== 'type' && attr.name !== 'data-cookie-category') {
        newScript.setAttribute(attr.name, attr.value)
      }
    })

    // Set correct type
    newScript.type = 'text/javascript'

    // Copy content
    const scriptSrc = oldScript.src || 'inline script'
    if (oldScript.src) {
      newScript.src = oldScript.src
      onUnblock?.(oldScript.src)
    } else if (oldScript.textContent) {
      newScript.textContent = oldScript.textContent
      onUnblock?.('inline script')
    }

    // Track unblocked script
    UNBLOCKED_SCRIPTS.push({
      src: scriptSrc,
      category,
      timestamp: Date.now(),
    })

    // Replace in DOM
    oldScript.parentNode?.replaceChild(newScript, oldScript)
    BLOCKED_SCRIPTS.delete(oldScript)

    debugLog('Script unblocked', newScript.src || 'inline')
  })

  debugLog(`Unblocked ${scriptsToUnblock.length} scripts`)
}

/**
 * Unblock iframes based on consent
 */
export function unblockIframes(consent: ConsentState): void {
  if (typeof document === 'undefined') return

  const iframesToUnblock: HTMLIFrameElement[] = []

  BLOCKED_IFRAMES.forEach(iframe => {
    const category = iframe.dataset.cookieCategory
    if (!category) return

    // Check if user consented to this category
    if (consent[category]) {
      iframesToUnblock.push(iframe)
    }
  })

  // Restore iframe sources
  iframesToUnblock.forEach(iframe => {
    const originalSrc = iframe.dataset.originalSrc
    if (originalSrc) {
      iframe.src = originalSrc
      delete iframe.dataset.originalSrc
      BLOCKED_IFRAMES.delete(iframe)

      debugLog('Iframe unblocked', originalSrc)
    }
  })

  debugLog(`Unblocked ${iframesToUnblock.length} iframes`)
}

/**
 * Initialize script blocking (call on page load)
 */
export function initScriptBlocking(): void {
  blockScripts()
  blockIframes()
}

/**
 * Apply consent to blocked elements
 */
export function applyConsent(consent: ConsentState, onUnblock?: (src: string) => void): void {
  unblockScripts(consent, onUnblock)
  unblockIframes(consent)
}

/**
 * Setup observer for dynamically added scripts/iframes
 */
export function observeNewElements(
  consent: ConsentState,
  onUnblock?: (src: string) => void
): MutationObserver | null {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return null

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        // Check if added node is a script with category
        if (
          node instanceof HTMLScriptElement &&
          node.dataset.cookieCategory &&
          node.type === 'text/plain'
        ) {
          const category = node.dataset.cookieCategory

          // If already consented, unblock immediately
          if (consent[category]) {
            const newScript = document.createElement('script')
            newScript.type = 'text/javascript'

            Array.from(node.attributes).forEach(attr => {
              if (attr.name !== 'type' && attr.name !== 'data-cookie-category') {
                newScript.setAttribute(attr.name, attr.value)
              }
            })

            if (node.src) {
              newScript.src = node.src
              onUnblock?.(node.src)
            } else if (node.textContent) {
              newScript.textContent = node.textContent
              onUnblock?.('inline script')
            }

            node.parentNode?.replaceChild(newScript, node)
            debugLog('Dynamically added script unblocked', newScript.src || 'inline')
          } else {
            BLOCKED_SCRIPTS.add(node)
            debugLog('Dynamically added script blocked', node.src || 'inline')
          }
        }

        // Check if added node is an iframe with category
        if (node instanceof HTMLIFrameElement && node.dataset.cookieCategory) {
          const category = node.dataset.cookieCategory

          if (consent[category]) {
            // Already consented, allow iframe
            debugLog('Dynamically added iframe allowed', node.src)
          } else {
            // Block iframe
            node.dataset.originalSrc = node.src
            node.src = ''
            BLOCKED_IFRAMES.add(node)
            debugLog('Dynamically added iframe blocked', node.dataset.originalSrc)
          }
        }
      })
    })
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })

  debugLog('MutationObserver initialized for dynamic elements')

  return observer
}

/**
 * Get debug info about blocked scripts
 */
export function getBlockedScriptsDebugInfo() {
  const blocked: { src: string; category: string }[] = []
  const blockedIframes: { src: string; category: string }[] = []

  BLOCKED_SCRIPTS.forEach(script => {
    blocked.push({
      src: script.src || script.textContent?.substring(0, 50) || 'inline',
      category: script.dataset.cookieCategory || 'unknown',
    })
  })

  BLOCKED_IFRAMES.forEach(iframe => {
    blockedIframes.push({
      src: iframe.dataset.originalSrc || iframe.src || 'unknown',
      category: iframe.dataset.cookieCategory || 'unknown',
    })
  })

  return {
    blockedScripts: blocked,
    blockedIframes,
    unblockedScripts: UNBLOCKED_SCRIPTS,
    totalBlocked: blocked.length + blockedIframes.length,
    totalUnblocked: UNBLOCKED_SCRIPTS.length,
  }
}
